import type { ConceptGraph, GraphNode } from '../api/knowledgeApi'

import { defaultGraphTheme, type GraphTheme } from './graphTheme'

// The same categorical slots used by the admin's existing activity and traffic charts.
const CATEGORY_SLOTS: Record<string, keyof GraphTheme['palette']> = {
  style: 'violet',
  color: 'pink',
  technique: 'blue',
  composition: 'forest',
  theme: 'green',
  subject: 'yellow',
  mood: 'orange',
  medium: 'violet',
  collector_appeal: 'blue',
  movement: 'violet',
  place: 'green',
  object: 'yellow',
  figure: 'orange',
  experience: 'pink',
}
export const isQuery = (node: GraphNode) => node.role === 'query' || (node.centre && !node.ucid)
export const nodeColor = (node: GraphNode, theme: GraphTheme = defaultGraphTheme) =>
  isQuery(node)
    ? theme.foreground
    : node.kind === 'artwork'
      ? theme.gold
      : theme.palette[CATEGORY_SLOTS[node.category || '']] || theme.mutedForeground
export const nodeKind = (node: GraphNode) =>
  isQuery(node)
    ? 'Search query'
    : node.kind === 'artwork'
      ? 'Artwork'
      : (node.category || 'Concept').replace(/_/g, ' ')
export const relationLabel = (kind: string) =>
  ({
    embodies: 'Appears in',
    near_duplicate: 'Nearly identical',
    strongly_related: 'Strong connection',
    related: 'Related',
    loosely_related: 'Loose connection',
    weak: 'Subtle connection',
  })[kind] || kind.replace(/_/g, ' ')

export function connections(graph: ConceptGraph) {
  const adjacent = new Map<string, Set<string>>()
  for (const edge of graph.edges) {
    if (!adjacent.has(edge.source)) adjacent.set(edge.source, new Set())
    if (!adjacent.has(edge.target)) adjacent.set(edge.target, new Set())
    adjacent.get(edge.source)!.add(edge.target)
    adjacent.get(edge.target)!.add(edge.source)
  }
  return adjacent
}

/** Keep the centre as an anchor; artworks must connect to the selected concept category. */
export function filterGraph(
  graph: ConceptGraph,
  category: string,
  showArtworks: boolean
): ConceptGraph {
  const concepts = new Set(
    graph.nodes
      .filter(
        (n) =>
          n.centre ||
          n.id === graph.centre ||
          (n.kind === 'concept' && (!category || (n.category || 'uncategorised') === category))
      )
      .map((n) => n.id)
  )
  const evidence = category
    ? new Set(
        graph.nodes
          .filter(
            (n) =>
              n.kind === 'concept' && !isQuery(n) && (n.category || 'uncategorised') === category
          )
          .map((n) => n.id)
      )
    : concepts
  const artworkIds = new Set<string>()
  if (showArtworks)
    for (const edge of graph.edges) {
      // A centre kept only as an anchor is not evidence for a different category.
      if (evidence.has(edge.source)) artworkIds.add(edge.target)
      if (evidence.has(edge.target)) artworkIds.add(edge.source)
    }
  const nodes = graph.nodes.filter((n) =>
    n.kind === 'artwork' ? showArtworks && (!category || artworkIds.has(n.id)) : concepts.has(n.id)
  )
  const ids = new Set(nodes.map((n) => n.id))
  return {
    ...graph,
    nodes,
    edges: graph.edges.filter((e) => ids.has(e.source) && ids.has(e.target)),
  }
}

export type GraphLens = 'all' | 'ontology' | 'similarity' | 'artwork' | 'search'
export const isOntology = (kind: string) =>
  ['broader_than', 'variant_of', 'opposite_of', 'related_to'].includes(kind)
export const isDirectional = (kind: string) =>
  ['broader_than', 'variant_of', 'embodies', 'search_result', 'search_match'].includes(kind)
export const edgeFamily = (kind: string) =>
  isOntology(kind)
    ? 'ontology'
    : kind === 'embodies'
      ? 'artwork'
      : kind.startsWith('search_')
        ? 'search'
        : 'similarity'

/** A lens restricts assertions, never creates them. Retain isolated concepts for context. */
export function lensGraph(graph: ConceptGraph, lens: GraphLens): ConceptGraph {
  if (lens === 'all') return graph
  const edges = graph.edges.filter((e) => edgeFamily(e.kind) === lens)
  const connected = new Set(edges.flatMap((e) => [e.source, e.target]))
  return {
    ...graph,
    edges,
    nodes: graph.nodes.filter((n) => n.kind !== 'artwork' || connected.has(n.id)),
  }
}

/** Labels read from the selected node, so stored edge direction remains meaningful. */
export function relationFrom(kind: string, outgoing: boolean) {
  const labels: Record<string, [string, string]> = {
    broader_than: ['broader than', 'narrower than'],
    variant_of: ['variant of', 'has variant'],
    opposite_of: ['opposite of', 'opposite of'],
    related_to: ['related to', 'related to'],
    embodies: ['embodies', 'appears in'],
    search_result: ['returns artwork', 'returned for query'],
    search_match: ['reaches concept', 'reached by query'],
  }
  return labels[kind]?.[outgoing ? 0 : 1] || relationLabel(kind)
}

/** Older Picasso snapshots encode search results and concept→artwork links as "embodies". */
export function semanticGraph(graph: ConceptGraph): ConceptGraph {
  const nodes = new Map(graph.nodes.map((n) => [n.id, n]))
  return {
    ...graph,
    edges: graph.edges.map((edge) => {
      const source = nodes.get(edge.source),
        target = nodes.get(edge.target)
      if ((source && isQuery(source)) || (target && isQuery(target))) {
        const other = source && isQuery(source) ? target : source
        return {
          ...edge,
          kind: other?.kind === 'artwork' ? 'search_result' : 'search_match',
          basis: 'search',
        }
      }
      if (edge.kind === 'embodies' && source?.kind === 'concept' && target?.kind === 'artwork')
        return { ...edge, source: edge.target, target: edge.source }
      return edge
    }),
  }
}
